import React, { useState, useEffect } from 'react';
import { Button } from '@/ui/components';
import { Input } from '@/ui/components';
import { Label } from '@/ui/components';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components';
import { X, Search, MapPin } from '@/ui/icons'
import { useRoutes } from '@/modules/routes';
import { useRouteAssociations } from '@/modules/routes';

interface AddRouteToTripModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripId: string;
  onRouteAdded?: () => void;
}

export const AddRouteToTripModal: React.FC<AddRouteToTripModalProps> = ({
  isOpen,
  onClose,
  tripId,
  onRouteAdded
}) => {
  const { routes, loading: routesLoading, fetchRoutes } = useRoutes();
  const { associateRouteToTrip, loading: associating } = useRouteAssociations();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [associatedRouteIds, setAssociatedRouteIds] = useState<Set<string>>(new Set());
  const [loadingAssociatedRoutes, setLoadingAssociatedRoutes] = useState(false);

  // Load routes and associated routes when modal opens
  useEffect(() => {
    if (isOpen && tripId) {
      const loadData = async () => {
        try {
          setLoadingAssociatedRoutes(true);
          
          // Routes are now drawn client-side, not fetched from backend
          // This modal may need to be updated or removed since routes are client-side
          setAssociatedRouteIds(new Set());
          
          // Routes are client-side, so we can't fetch them
          // Note: routes state is managed by useRoutes hook, we can't set it here
        } catch (error) {
        } finally {
          setLoadingAssociatedRoutes(false);
        }
      };
      
      loadData();
    }
    // Note: Routes are client-side, so we don't fetch them on modal open
  }, [isOpen, tripId]);

  // Filter routes based on search term
  const filteredRoutes = routes.filter(route =>
    route.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddRoute = async () => {
    if (!selectedRouteId) {
      return;
    }

    // Check if route is already associated
    if (associatedRouteIds.has(selectedRouteId)) {
      return;
    }

    try {
      await associateRouteToTrip(selectedRouteId, tripId);
      setSelectedRouteId(null);
      setSearchTerm('');
      // Update the set of associated routes
      setAssociatedRouteIds(prev => new Set(prev).add(selectedRouteId));
      onRouteAdded?.();
      onClose();
    } catch (error) {
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Add Route to Trip
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* Search Input */}
          <div className="space-y-2">
            <Label htmlFor="search">Search Routes</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or description..."
                className="pl-10"
              />
            </div>
          </div>

          {/* Routes List */}
          {routesLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Loading routes...</p>
            </div>
          ) : filteredRoutes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchTerm ? 'No routes match your search' : 'No routes available'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRoutes.map((route) => (
                <div
                  key={route.id}
                  onClick={() => !associatedRouteIds.has(route.id) && setSelectedRouteId(route.id)}
                  className={`p-3 border rounded-lg transition-colors ${
                    associatedRouteIds.has(route.id)
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : selectedRouteId === route.id
                      ? 'border-adventure-500 bg-adventure-50 cursor-pointer'
                      : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{route.name}</h4>
                        {associatedRouteIds.has(route.id) && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Already Added</span>
                        )}
                      </div>
                      {route.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{route.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>{route.waypoint_count || 0} waypoints</span>
                        {route.distance && (
                          <span>{(route.distance / 1000).toFixed(1)} km</span>
                        )}
                        <span>{route.is_public ? 'Public' : 'Private'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={associating || loadingAssociatedRoutes}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddRoute}
              disabled={!selectedRouteId || associating || loadingAssociatedRoutes || (!!selectedRouteId && associatedRouteIds.has(selectedRouteId))}
              className="flex-1"
            >
              {associating && selectedRouteId && !loadingAssociatedRoutes ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                'Add Route'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddRouteToTripModal;

